/**
 * Policy Content Navigation
 * -------------------------
 * - Builds the sidebar TOC from the rendered policy groups (each Policy Group
 *   block emits a [data-toc-section] wrapper and a .policy-group__heading).
 *   The sidebar is built at runtime because Horizon's theme-block architecture
 *   doesn't expose nested theme-block settings to the parent section's Liquid.
 * - Smooth-scrolls when a sidebar link is clicked, accounting for any sticky
 *   header via the section's scroll-offset setting.
 * - Highlights the active sidebar item using an IntersectionObserver so the
 *   highlight follows the section currently nearest the top of the viewport.
 * - On mobile, the sidebar collapses into a dropdown; the toggle is wired up
 *   here and the list is shown/hidden via the [data-toc-open] attribute.
 * - Watches the content area for block edits in the theme editor and rebuilds
 *   the sidebar whenever a policy group is added, removed, or its heading
 *   changes.
 */

class PolicyContentNavigation extends HTMLElement {
  connectedCallback() {
    this.tocList = this.querySelector('[data-toc-list]');
    this.tocAside = this.querySelector('[data-toc-aside]');
    this.scope = this.querySelector('[data-toc-scope]');
    this.toggle = this.querySelector('[data-toc-toggle]');
    this.toggleLabel = this.querySelector('[data-toc-toggle-label]');

    if (!this.tocList || !this.scope) return;

    this.scrollOffset = parseInt(this.dataset.scrollOffset, 10) || 100;
    this.activeAnchor = null;
    this.defaultToggleLabel = this.toggleLabel?.textContent?.trim() || 'Jump to section';

    this.handleClick = this.handleClick.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.rebuild = this.rebuild.bind(this);

    this.tocList.addEventListener('click', this.handleClick);
    this.tocList.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('hashchange', this.handleHashChange);

    if (this.toggle) {
      this.toggle.addEventListener('click', this.handleToggle);
      document.addEventListener('click', this.handleDocumentClick);
    }

    this.build();
    this.observeContentChanges();
  }

  disconnectedCallback() {
    this.tocList?.removeEventListener('click', this.handleClick);
    this.tocList?.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('hashchange', this.handleHashChange);

    if (this.toggle) {
      this.toggle.removeEventListener('click', this.handleToggle);
      document.removeEventListener('click', this.handleDocumentClick);
    }

    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  /** Collect the rendered policy groups and rebuild the sidebar + observer. */
  build() {
    this.sections = Array.from(this.scope.querySelectorAll('[data-toc-section]'));

    // Ensure every section has a stable anchor on its host element.
    this.sections.forEach((section) => {
      if (!section.id) section.id = section.dataset.tocSection || '';
    });

    this.renderTocItems();

    this.links = Array.from(this.tocList.querySelectorAll('[data-toc-target]'));
    this.linkByAnchor = new Map(this.links.map((link) => [link.dataset.tocTarget, link]));

    this.setupIntersectionObserver();

    // Show/hide the sidebar based on whether we have any items.
    if (this.tocAside) {
      const hasItems = this.sections.length > 0;
      this.tocAside.hidden = !hasItems;
      this.classList.toggle('policy-content-navigation--no-toc', !hasItems);
    }

    // Restore active state if the URL already targets one of our sections.
    if (window.location.hash) {
      const anchor = window.location.hash.replace('#', '');
      if (this.linkByAnchor.has(anchor)) {
        this.setActive(anchor, { updateLabel: true });
      }
    }
  }

  rebuild() {
    // Tear down the existing observer; the new build will create a fresh one.
    this.intersectionObserver?.disconnect();
    this.activeAnchor = null;
    if (this.toggleLabel) this.toggleLabel.textContent = this.defaultToggleLabel;
    this.build();
  }

  renderTocItems() {
    // Clear and repopulate.
    while (this.tocList.firstChild) this.tocList.removeChild(this.tocList.firstChild);

    if (!this.sections.length) return;

    const fragment = document.createDocumentFragment();

    this.sections.forEach((section) => {
      const anchor = section.dataset.tocSection || section.id;
      if (!anchor) return;

      const heading =
        section.querySelector('.policy-group__heading')?.textContent?.trim() ||
        section.getAttribute('aria-label') ||
        anchor;

      const li = document.createElement('li');
      li.className = 'policy-content-navigation__toc-item';

      const a = document.createElement('a');
      a.className = 'policy-content-navigation__toc-link';
      a.href = `#${anchor}`;
      a.dataset.tocTarget = anchor;

      const text = document.createElement('span');
      text.className = 'policy-content-navigation__toc-text';
      text.textContent = heading;

      a.appendChild(text);
      li.appendChild(a);
      fragment.appendChild(li);
    });

    this.tocList.appendChild(fragment);
  }

  /** Re-build the sidebar when blocks are added, removed, or edited. */
  observeContentChanges() {
    if (typeof MutationObserver === 'undefined') return;

    this.mutationObserver = new MutationObserver(() => {
      // Coalesce rapid mutations into a single rebuild per frame.
      if (this.rebuildScheduled) return;
      this.rebuildScheduled = true;
      requestAnimationFrame(() => {
        this.rebuildScheduled = false;
        this.rebuild();
      });
    });

    this.mutationObserver.observe(this.scope, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  setupIntersectionObserver() {
    if (typeof IntersectionObserver === 'undefined' || !this.sections.length) return;

    // A narrow band near the top of the viewport. Whichever section is most
    // visible inside that band wins the active state.
    const topMargin = Math.max(this.scrollOffset - 8, 0);
    const bottomMargin = Math.max(window.innerHeight - this.scrollOffset - 80, 200);

    this.visible = new Map();
    this.intersectionObserver = new IntersectionObserver(
      (entries) => this.onIntersect(entries),
      {
        rootMargin: `-${topMargin}px 0px -${bottomMargin}px 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    this.sections.forEach((section) => this.intersectionObserver.observe(section));
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      const anchor = entry.target.dataset.tocSection;
      if (entry.isIntersecting) {
        this.visible.set(anchor, entry.intersectionRatio);
      } else {
        this.visible.delete(anchor);
      }
    });

    if (this.visible.size === 0) {
      const nearest = this.findNearestSection();
      if (nearest) this.setActive(nearest, { updateLabel: true });
      return;
    }

    // Pick the section with the highest visibility ratio. Ties resolve to
    // the section that appears earliest in the document.
    let bestAnchor = null;
    let bestRatio = -1;
    let bestOrder = Infinity;

    this.visible.forEach((ratio, anchor) => {
      const order = this.sections.findIndex((s) => s.dataset.tocSection === anchor);
      if (ratio > bestRatio || (ratio === bestRatio && order < bestOrder)) {
        bestAnchor = anchor;
        bestRatio = ratio;
        bestOrder = order;
      }
    });

    if (bestAnchor) this.setActive(bestAnchor, { updateLabel: true });
  }

  findNearestSection() {
    let nearest = null;
    let nearestDistance = Infinity;

    this.sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const distance = Math.abs(rect.top - this.scrollOffset);
      if (rect.bottom > 0 && rect.top < window.innerHeight && distance < nearestDistance) {
        nearestDistance = distance;
        nearest = section.dataset.tocSection;
      }
    });

    return nearest;
  }

  setActive(anchor, { updateLabel = false } = {}) {
    if (anchor === this.activeAnchor) return;
    this.activeAnchor = anchor;

    this.links.forEach((link) => {
      const isActive = link.dataset.tocTarget === anchor;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'location');
        if (updateLabel && this.toggleLabel) {
          this.toggleLabel.textContent = link.textContent.trim() || this.defaultToggleLabel;
        }
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  handleClick(event) {
    const link = event.target.closest('[data-toc-target]');
    if (!link) return;

    const anchor = link.dataset.tocTarget;
    const target = this.scope.querySelector(`[data-toc-section="${CSS.escape(anchor)}"]`);
    if (!target) return;

    event.preventDefault();
    this.scrollToSection(target, anchor);
    this.closeMobile();
  }

  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const link = event.target.closest('[data-toc-target]');
    if (!link) return;
    if (event.key === ' ') {
      event.preventDefault();
      link.click();
    }
  }

  scrollToSection(target, anchor) {
    const top = target.getBoundingClientRect().top + window.pageYOffset - this.scrollOffset;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top,
      behavior: prefersReduced ? 'auto' : 'smooth',
    });

    if (history.replaceState) {
      history.replaceState(null, '', `#${anchor}`);
    }

    this.setActive(anchor, { updateLabel: true });
  }

  handleToggle(event) {
    event.stopPropagation();
    if (!this.tocAside) return;

    const isOpen = this.tocAside.getAttribute('data-toc-open') === 'true';
    const next = !isOpen;
    this.tocAside.setAttribute('data-toc-open', String(next));
    this.toggle.setAttribute('aria-expanded', String(next));
  }

  handleDocumentClick(event) {
    if (!this.tocAside) return;
    if (this.tocAside.getAttribute('data-toc-open') !== 'true') return;
    if (this.tocAside.contains(event.target)) return;
    this.closeMobile();
  }

  closeMobile() {
    if (!this.tocAside) return;
    if (this.tocAside.getAttribute('data-toc-open') === 'true') {
      this.tocAside.setAttribute('data-toc-open', 'false');
      this.toggle?.setAttribute('aria-expanded', 'false');
    }
  }

  handleHashChange() {
    const anchor = window.location.hash.replace('#', '');
    if (!anchor || !this.linkByAnchor.has(anchor)) return;

    const target = this.scope.querySelector(`[data-toc-section="${CSS.escape(anchor)}"]`);
    if (target) this.scrollToSection(target, anchor);
  }
}

if (!customElements.get('policy-content-navigation')) {
  customElements.define('policy-content-navigation', PolicyContentNavigation);
}